import type { ApproachResult } from "./approach.ts";
import type { CorrectionFinding, CorrectionKind, CorrectionLessonSnapshotEntry, CorrectionStatus } from "./corrections.ts";
import type { LessonDTO } from "./lessons.ts";
import type { CheckTarget } from "./check-targets.ts";
import type { CheckDurations } from "./delivery-pipeline.ts";
import type { ProjectReviewSetting } from "./review-mode.ts";
import type { RouteSuggestion } from "./routing.ts";
import type { RoutineWindow } from "./schedules.ts";
import type { DeliverableKind, EvidencePayload, VerificationResult } from "./verification.ts";

/** GET /api/where: which process served this page. Never carries the hub URL, tokens or ports. */
export type WhereDTO =
  | { role: "hub"; hub: { name: string } }
  | { role: "node"; hub: { name: string }; node: { id: string; name: string; owner: string; sameMachineAsHub: boolean } };
import type { OverlapWarning } from "./overlap.ts";
import type { SteerItem } from "./bridge.ts";
import type { DelegationBlock, DelegationPolicy } from "./delegation.ts";
import type { ChurnEntry } from "./workers/types.ts";
import type {
  AcceptanceCriterion,
  Actor,
  CloudLoginState,
  DecisionKind,
  Deliverable,
  HostState,
  Mode,
  Priority,
  RiskAssessment,
  RiskFlag,
  RoutineProposalState,
  RoutineReadScope,
  RoutineRunTrigger,
  RoutineStatus,
  Rule,
  Runtime,
  Size,
  Stage,
  TaskKind,
  Tier,
} from "./types.ts";

/** Shapes returned by the HTTP API and MCP tools. Shared by server and web. */

/** GET /api/hub/capabilities: what this hub can do, for the onboarding wizard. Never names a provider or carries credentials. */
export interface HubCapabilitiesDTO {
  /** "enabled": a provisioner and stored Vercel credentials; "needs_credentials": a provisioner but no credentials yet; "disabled": no provisioner. */
  remoteRuntime: "enabled" | "needs_credentials" | "disabled";
  /** The adapters the remote runtime can run (today only claude-code); empty unless it is enabled. */
  remoteAdapters: Runtime[];
}

/** GET /api/actors/:id/sandbox (and resume / check-login): a vercel-sandbox agent's sandbox at a glance. */
export interface SandboxStatusDTO {
  /** The provider-side sandbox name (derived from the agent id). Null only if a caller has no name to report. */
  sandboxName: string | null;
  /** The provider's session state; null when unknown (no credentials stored, or the sandbox does not exist yet). */
  state: HostState | null;
  /**
   * The last login probe's result; null until one has run since the hub started, after 10 minutes, and after a
   * failed probe or a recreated sandbox. The probe (`claude auth status`) cannot see an expired login, so it
   * answers 'ok' or 'missing' only; 'expired' is not produced by check-login.
   */
  loginState: CloudLoginState | null;
  /** When the hub last saw the sandbox stop with a snapshot (epoch ms); null if not seen since the hub started. */
  lastSnapshotAt: number | null;
}

export type ActorDTO = Actor & {
  lastSeenAt: number | null;
  activity: string | null;
  createdAt: number;
  online: boolean;
  /** A human's standing instruction to let their own agent decide some reviews alone. Null = off. Always null for an agent actor. */
  delegationPolicy: DelegationPolicy | null;
  /** Projects this actor is assigned to. Empty = all projects, including future ones. */
  projectIds: string[];
};

/** A minted token without its secret. Mint returns the secret once; listing never does. */
export type TokenDTO = {
  id: string;
  label: string;
  /** `automation` or empty. The label is not a permission. */
  scope: string;
  createdAt: number;
  revokedAt: number | null;
};

export const PROJECT_KINDS = ["saas", "agent", "website", "library", "other"] as const;
export type ProjectKind = (typeof PROJECT_KINDS)[number];

export type ProjectDTO = {
  id: string;
  name: string;
  kind: ProjectKind;
  description: string;
  repoUrl: string | null;
  localPath: string | null;
  /** Run before a done task's branch is merged into main; a non-zero exit fails the merge. */
  checkCommand: string | null;
  /**
   * Tiered checks (A4a): when set (and checkCommand is too), the merge queue runs this instead of checkCommand,
   * with ROOMATE_MERGE_BASE set to main's sha before the merge, and checkCommand runs on main itself once
   * `fullCheckEveryMerges` merges or `fullCheckEveryMs` have gone by since the last full check. Null = checkCommand everywhere.
   */
  fastCheckCommand: string | null;
  /** Merges on main after which a full check is due (fast projects only); default 4. */
  fullCheckEveryMerges: number;
  /** Time after the last full check at which unchecked merges make a full check due (fast projects only); default 30 minutes. */
  fullCheckEveryMs: number;
  /** Week-by-week on-call rotation, actor ids in order. Empty = escalation never fires. */
  onCallRotation: string[];
  /** Week 0 of `onCallRotation`. Null until the rotation is first set. */
  onCallAnchorAt: number | null;
  /** Wall-clock minutes a delegated review may sit unanswered before escalating to on-call. */
  reviewIdleWindowMin: number;
  /** Monthly USD budget (MC10a). Null = no budget. Spend is summed from the project's runs for the UTC month. */
  monthlyBudgetUsd: number | null;
  /** How a node gets the repo. Null = legacy project mapped by hand in node.json. */
  repoMode: "fresh" | "url" | "path" | null;
  repoState: "pending" | "ready" | "error" | null;
  repoError: string | null;
  /** Node that owns the git snapshot and merge queue for this project. */
  homeNodeId: string | null;
  /** When an admin archived the project; null = active. Archived projects keep all data but take no new work. */
  archivedAt: number | null;
  /** Landing A1: agents stop claiming new tasks while this many done tasks wait to merge. Null = no cap. */
  mergeWipCap: number | null;
  /** Done tasks waiting for the merge queue right now, against the cap. */
  mergeBacklog: { queued: number; cap: number | null };
  /** Per-project delivery policy (docs/specs/delivery-policy.md); only conflict resolution is stored so far. */
  delivery: { conflictResolution: ConflictResolution; /** `owner_pr`: Roomate never merges, the node reports the person's merge (github-machine-cli.md §6.3); absent = `office`. */ merge?: DeliveryMerge };
  /**
   * Who runs this project's GitHub pull-request flow (github-machine-cli.md §7.4): `hub` = the hub's own path (default), `node` =
   * the node, through `gh`, reporting `POST /api/tasks/:id/pr`. Admin-set per project, so the two paths never run together for one project.
   */
  githubFlow: GithubFlow;
  /** Who answers this project's reviews (landing C1). Nulls inherit the office setting; see `effectiveReviewPolicy`. */
  review: ProjectReviewSetting;
  /** Whether the hub's GitHub App covers this project's github.com repo (docs/specs/github-app.md §4.2). */
  github: ProjectGithub;
  /** Where merged work goes: `local` (never pushes), `remote` (push main), `pr` (a PR per task). Null = legacy node.json behaviour. */
  pushMode: PushMode | null;
  /** Latest dry-run push check on the merging node; null until one reports. */
  pushCheck: PushCheck | null;
  /** Each reporting node's own push check and `gh` repo access for this project (one entry per node); absent from an older hub. */
  nodeChecks?: ProjectNodeCheckDTO[];
  /**
   * Set while the hub cannot get GitHub tokens and this bound remote or pr project's merges are paused
   * (docs/specs/github-app.md §4.9): `github_unavailable` resumes automatically, `github_unlinked` needs an
   * admin. Null (or absent) otherwise; local projects keep merging and never carry it.
   */
  githubPause?: GitHubPause | null;
  createdAt: number;
};

/**
 * What one node found for a project (docs/specs/github-machine-cli.md §5.2): its own dry-run push check and its `gh repo view`
 * write-permission result. Kept per node so "node A can push, node B cannot" is visible; `ProjectDTO.pushCheck` stays the
 * merging node's latest. Messages are redacted and capped before they are stored.
 */
export type ProjectNodeCheckDTO = {
  nodeId: string;
  push: PushCheck | null;
  ghRepoAccess: { ok: boolean; at: number; nodeId: string; message: string | null } | null;
};

export type GitHubPause = { state: "github_unavailable" | "github_unlinked"; message: string; retryAt: number | null };

export const PUSH_MODES = ["local", "remote", "pr"] as const;
export type PushMode = (typeof PUSH_MODES)[number];

/** How local `main` relates to the remote's: `ahead`/`equal` push fast-forward; `diverged` cannot; `error` is a failed dry run. */
export const PUSH_CHECK_RESULTS = ["ahead", "equal", "diverged", "error"] as const;
export type PushCheckResult = (typeof PUSH_CHECK_RESULTS)[number];

/** `ok` is true only for `ahead` and `equal`: a `diverged` check is a failing check. A check made stale (repo changed) has `ok: false`. */
export type PushCheck = { ok: boolean; result: PushCheckResult | "stale"; at: number; nodeId: string | null; message: string | null };

/**
 * Why a project is not bound to the GitHub App: its repo is not on github.com (`not_github`), no app is
 * connected (`no_app`), no installation covers the repo (`not_installed`), or the covering installation
 * is suspended on GitHub (`suspended`).
 */
export const GITHUB_UNBOUND_REASONS = ["not_github", "no_app", "not_installed", "suspended"] as const;
export type GithubUnboundReason = (typeof GITHUB_UNBOUND_REASONS)[number];

/**
 * A project's GitHub binding (§4.2). `fullName` is the parsed `owner/repo` whenever the URL is a github.com
 * repo, bound or not; `installationAccount` names the installation covering it. `botName` and `botEmail`
 * are the app's commit identity, null until an app with a known bot user is connected. `reason` is null
 * exactly when `bound` is true.
 */
export type ProjectGithub = {
  bound: boolean;
  fullName: string | null;
  installationAccount: string | null;
  appSlug: string | null;
  botName: string | null;
  botEmail: string | null;
  reason: GithubUnboundReason | null;
  /**
   * The node flow's view (github-machine-cli.md §7.1): `repo` is the `owner/repo` and `host` the GitHub host of the project's
   * repo_url (null when it is not a GitHub-style https url), `capability` the merging node's `github:<host>` state ("unknown"
   * until it reports). The hub holds no installation, app or token for any of it.
   */
  repo: string | null;
  host: string | null;
  capability: "connected" | "not_logged_in" | "not_installed" | "unknown";
};

/**
 * Who brings a branch up to date when the merge queue hits a conflict (landing A1, delivery-policy §2.1):
 * `office` files a resolve-merge task for the office's merge agent (today's behaviour), `builder` sends the
 * task back to its builder, who merges main into the branch in the same worktree and session, and `off`
 * files the resolve task but assigns nobody.
 */
export const CONFLICT_RESOLUTIONS = ["office", "builder", "off"] as const;
export type ConflictResolution = (typeof CONFLICT_RESOLUTIONS)[number];

/** Why a done task went back to its builder: the merge queue found a conflict with main (landing A1). */
export type MergeRework = {
  kind: "conflict";
  /** Conflicting paths from the merge output; empty when git named none. */
  files: string[];
  /** The merge output, clipped. */
  output: string;
  at: number;
  /** 1 on the first bounce; the hub stops bouncing after MERGE_REWORK_MAX and files a resolve task instead. */
  attempt: number;
};

export const MERGE_STATES = ["merged", "merge_failed", "abandoned"] as const;
export type MergeState = (typeof MERGE_STATES)[number];
/** #510: the two ways origin's branch can disagree with what was approved. */
export const APPROVAL_ISSUES = ["changed_after_approval", "not_on_origin"] as const;
export type ApprovalIssue = (typeof APPROVAL_ISSUES)[number];

const APPROVAL_ISSUE_SET: ReadonlySet<string> = new Set(APPROVAL_ISSUES);

/** True only for the two `ApprovalIssue` codes. Inherited names such as `"constructor"` are not issues. */
export function isApprovalIssue(value: unknown): value is ApprovalIssue {
  return typeof value === "string" && APPROVAL_ISSUE_SET.has(value);
}

/**
 * Badge copy, event notes, stage reasons, and merge-queue logs. One map so those cannot drift.
 * `not_on_origin` is any approved commit origin cannot see: a push that never arrived, rewritten
 * history, or a deleted branch. The log line does not guess which of those it was.
 */
export const APPROVAL_ISSUE_LOG: Record<ApprovalIssue, string> = {
  changed_after_approval: "changed after approval",
  not_on_origin: "approved commit not on origin",
};

export type IntentStatus = "draft" | "spec_review" | "approved" | "done" | "cancelled";

export type IntentDTO = {
  id: string;
  projectId: string;
  title: string;
  kind: TaskKind;
  problem: string;
  specMd: string;
  acceptanceCriteria: AcceptanceCriterion[];
  status: IntentStatus;
  priority: Priority;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** `idea`: filed in one line by a human; an agent writes the spec and approval returns to that human. */
  source: "manual" | "idea";
};

export type TaskDTO = {
  id: string;
  number: number;
  projectId: string;
  intentId: string | null;
  title: string;
  description: string;
  kind: TaskKind;
  /** What the task delivers (docs/specs/mission-control.md §3.2); the hub always sets it, `code` by default.
   * Optional in the type only so fixtures built before it existed still compile. */
  deliverable?: Deliverable;
  acceptanceCriteria: AcceptanceCriterion[];
  areas: string[];
  /** What a `check` task probes (docs/specs/mission-control.md §3.2 rule 5); `[]` for every other task.
   * The hub always sets it. Optional in the type only so fixtures built before it existed still compile. */
  checkTargets?: CheckTarget[];
  /** The schedule that created this task (docs/specs/mission-control.md §7 MC3), set by the hub only; null otherwise.
   * Optional in the type only so fixtures built before it existed still compile. */
  scheduleId?: string | null;
  /** Hub-owned execution context for a routine task; null for ordinary and interval-schedule tasks. */
  routineWindow?: RoutineWindow | null;
  /** Per-task run cap. Routine tasks set this from their routine; null uses the agent's normal cap. */
  maxBudgetUsd?: number | null;
  /** Capabilities a machine must have ready to take this task (`vercel`, `github:github.com`), as declared. GitHub work also derives `github:<host>` at routing time (docs/specs/machine-connected-services.md §7). Optional so older fixtures compile. */
  requiresServices?: string[];
  labels: string[];
  skills: string[];
  size: Size;
  flags: RiskFlag[];
  priority: Priority;
  dependsOn: string[];
  stage: Stage;
  blockedFrom: Stage | null;
  mode: Mode | null;
  builderId: string | null;
  reviewerIds: string[];
  risk: RiskAssessment;
  suggestion: RouteSuggestion | null;
  verification: (VerificationResult & { at: number }) | null;
  branch: string | null;
  /** Set once the merge queue has attempted this done task's branch, or a person retires it; null while it's still queued or not yet due. */
  mergeState: MergeState | null;
  mergedSha: string | null;
  mergeOutput: string | null;
  /** Set alongside `mergeOutput` on a `merge_failed` report: the branch tip the check actually ran
   * against, and the (unpublished) merge commit it ran against when the checkCommand got that far —
   * null on a straight conflict. Lets a stale report be caught against the branch's current tip. */
  mergeCheckedBranchSha: string | null;
  mergeCheckedMergedSha: string | null;
  /** The exact branch commit the evidence / review verdict vouched for (#510). The merge queue merges only this commit; null on tasks approved before this existed (they merge as before). */
  approvedSha?: string | null;
  /** The branch tip a merge node last saw on origin (#510); shown next to `approvedSha`. */
  originTipSha?: string | null;
  /** Why the merge queue refused an approved task (#510); null when nothing is wrong. */
  approvalIssue?: ApprovalIssue | null;
  /** Set while a merge conflict is back with the builder (landing A1); cleared by the next evidence. */
  mergeRework?: MergeRework | null;
  /** The GitHub pull request a pr-mode task merges through (github-app.md §4.6); null while it has none. */
  pullRequest?: TaskPullRequest | null;
  /** Why a node holds this task's PR flow right now (github-machine-cli.md §6.6): the task stays where it is; null when nothing holds it. */
  prHold?: TaskPrHold | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  stageChangedAt: number;
};

export const PR_STATES = ["open", "merged", "closed"] as const;
export type PrState = (typeof PR_STATES)[number];
export type TaskPullRequest = { number: number; url: string; state: PrState };
/** `card` is the hub's own catalog text for the reporting node's GitHub state (§5.3), never the node's words; `message` is the node's, redacted. */
export type TaskPrHold = { reason: string; message: string | null; card: string | null };

export const GITHUB_FLOWS = ["hub", "node"] as const;
export type GithubFlow = (typeof GITHUB_FLOWS)[number];
export const DELIVERY_MERGES = ["office", "owner_pr"] as const;
export type DeliveryMerge = (typeof DELIVERY_MERGES)[number];

/** Why a node holds a task's PR flow, as it classifies it (github-machine-cli.md §6.3 failures, §6.6). */
export const PR_REPORT_REASONS = ["branch_protection", "pr_closed", "merged_elsewhere", "task_cancelled", "push_failed", "github_not_ready", "rate_limited"] as const;
export type PrReportReason = (typeof PR_REPORT_REASONS)[number];

/**
 * `POST /api/tasks/:id/pr` (§6.4), node auth: facts the node saw on GitHub, never credentials. A report carries the PR facts
 * (`state`, `number`, `url`, `sha` together), a hold `reason`, or both. `sha` is the head (`open`; `closed`), the approved head
 * (a `pr` merge) or GitHub's merge commit (`merged` in `owner_pr`); `mergedSha` is the merge commit of a `pr` merge.
 * `message` is redacted by the node and again by the hub.
 */
export type PrReport = {
  nodeId: string;
  state?: PrState;
  number?: number;
  url?: string;
  sha?: string;
  mergedSha?: string;
  reason?: PrReportReason | null;
  message?: string | null;
};

export const PR_REPORT_MESSAGE_MAX = 2000;
const PR_HEX_SHA = /^[0-9a-f]{40}([0-9a-f]{24})?$/i;
export const isPrReportSha = (v: unknown): v is string => typeof v === "string" && PR_HEX_SHA.test(v);

/** Shape check shared by the node (before sending) and the hub (before validating): null when well-formed, else why not. */
export function prReportShapeError(r: Partial<PrReport>): string | null {
  const given = [r.state, r.number, r.url, r.sha].filter((v) => v !== undefined).length;
  if (given !== 0 && given !== 4) return "state, number, url and sha come together";
  if (given === 0 && !r.reason) return "a report needs the PR facts or a reason";
  if (r.state !== undefined && !(PR_STATES as readonly unknown[]).includes(r.state)) return "unknown state";
  if (r.number !== undefined && !(Number.isInteger(r.number) && r.number > 0)) return "number must be a positive integer";
  if (r.sha !== undefined && !isPrReportSha(r.sha)) return "sha must be a hex commit sha";
  if (r.mergedSha !== undefined && !(isPrReportSha(r.mergedSha) && r.state === "merged")) return "mergedSha is a hex sha and only goes with a merged report";
  if (r.reason != null && !(PR_REPORT_REASONS as readonly unknown[]).includes(r.reason)) return "unknown reason";
  return null;
}

/** One row of `GET /ideas`: an idea intent, its tasks, its spec task, and when its code reached main (#386). */
export type IdeaRowDTO = {
  intent: IntentDTO;
  tasks: TaskDTO[];
  specTask: TaskDTO | null;
  /** Null until every deliverable task has landed; abandoned work remains blocking. */
  shippedAt: number | null;
};

export type DecisionOption = { id: string; label: string; consequences: string };

/** Body of POST /decisions/:id/answer. Token scope, not this marker, decides automation. */
export type AnswerInput = {
  optionId: string;
  rationale?: string | null;
  branchSha?: string;
  automated?: boolean;
  /** A person's own tick (#832): also accept the open review findings the decision quotes. Refused for automation. */
  acceptFindings?: boolean;
};
export type DecisionStatus = "open" | "answered" | "cancelled";

/** What a delegate agent reported through `submit_review_verdict`, whichever branch followed. */
export type DelegateVerdict = {
  optionId: "approve" | "request_changes" | "ok" | "issue";
  rationale: string;
  findings: { severity: "critical" | "important" | "minor"; text: string }[];
  /** The review run that produced this verdict, when it ran as a tracked run. */
  runId: string | null;
  /** True iff this verdict answered the decision on its own; false means it only became a recommendation. */
  inPolicy: boolean;
  /** Every reason the verdict could not stand alone. Empty iff `inPolicy`. */
  blocks: DelegationBlock[];
  /** The commit the verdict was given on (#510); absent on verdicts recorded before it was required. */
  branchSha?: string;
  at: number;
};

/** The Chief's note on a decision (chief-of-office.md §12.0 A2). Stored apart from the decision; it never changes its options, assignee, status or answer. */
export type ChiefNoteDTO = {
  decisionId: string;
  chiefId: string;
  recommendation: string | null;
  confidence: number | null;
  packetLinks: { label: string; ref: string }[];
  createdAt: number;
};

export type DecisionDTO = {
  id: string;
  projectId: string;
  taskId: string | null;
  intentId: string | null;
  kind: DecisionKind;
  question: string;
  contextMd: string;
  options: DecisionOption[];
  recommendation: string | null;
  confidence: number | null;
  evidence: { label: string; ref: string }[];
  blocking: boolean;
  askedBy: string;
  assigneeId: string | null;
  tier: Tier;
  status: DecisionStatus;
  answer: {
    optionId: string;
    rationale: string;
    /** The accountable person; automation answers through that person's scoped token. */
    by: string;
    at: number;
    automated?: boolean;
    /** Trimmed token label used only for attribution. */
    source?: string;
  } | null;
  createdAt: number;
  answeredAt: number | null;
  priority: { score: number; ageMin: number; blockedMin: number };
  /** A short Hebrew rendering of the question and option labels, from the hub's local model. */
  he: { question: string; options: Record<string, string> } | null;
  /** The agent this decision was opened for, when its assignee has a delegation policy covering it. Null = no delegation; the decision behaves exactly as it always has. */
  delegateId: string | null;
  /** 'pending' while the agent's review run hasn't reported back; 'ran' once it has (in policy or not); 'failed' if the run closed without a verdict. Null iff `delegateId` is null. */
  delegateState: "pending" | "ran" | "failed" | null;
  /** How many review runs have been started for this decision's delegate. Bounds retry: a dispatcher stops at 2. */
  delegateAttempts: number;
  /** Set once the delegate has reported, whichever branch it took. */
  delegateVerdict: DelegateVerdict | null;
  /** Set once by the escalation sweeper (§4.9) when a person is still required past the idle window. Stamped once so a decision can never escalate twice. */
  escalatedAt: number | null;
  /** The Chief's note, when it left one; null otherwise. Read-only context: answering never looks at it. */
  chiefNote: ChiefNoteDTO | null;
};

export type EvidenceDTO = {
  id: string;
  taskId: string;
  submittedBy: string;
  payload: EvidencePayload;
  verdict: VerificationResult["verdict"];
  result: VerificationResult;
  createdAt: number;
};

export type BasedOn = { type: "spec" | "file" | "url" | "decision" | "doc" | "conversation" | "other"; ref: string; note?: string };

export type ProvenanceDTO = {
  id: string;
  projectId: string;
  taskId: string | null;
  actorId: string;
  summary: string;
  rationale: string;
  basedOn: BasedOn[];
  alternatives: { option: string; whyNot: string }[];
  confidence: number | null;
  createdAt: number;
};

export type EventDTO = {
  seq: number;
  ts: number;
  actorId: string | null;
  type: string;
  projectId: string | null;
  subjectType: string;
  subjectId: string;
  payload: Record<string, unknown>;
};

export type RuleDTO = Rule & { createdAt: number };

export type ProjectStatus = {
  project: ProjectDTO;
  stages: Record<Stage, number>;
  activeActors: { id: string; name: string; kind: Actor["kind"]; activity: string | null; taskId: string | null }[];
  openDecisions: number;
  oldestDecisionAgeMin: number | null;
  verification: { pass: number; fail: number; inconclusive: number; passRate: number | null };
  lastEvent: EventDTO | null;
  overlaps: number;
};

export type ProjectSummary = {
  stages: Record<Stage, number>;
  activeActors: ProjectStatus["activeActors"];
  openDecisions: number;
  lastActivityAt: number | null;
};

export type Overview = {
  projects: ProjectStatus[];
  online: { id: string; name: string; kind: Actor["kind"]; activity: string | null }[];
};

export type TaskDetail = {
  task: TaskDTO;
  intent: IntentDTO | null;
  decisions: DecisionDTO[];
  evidence: EvidenceDTO[];
  provenance: ProvenanceDTO[];
  timeline: EventDTO[];
  overlaps: OverlapWarning[];
  /** The builder's approach decision against the node-reported first commit (#544). Not required on low, no-code, merge-resolve and review-deliverable tasks. */
  approach: TaskApproach;
};

export type TaskApproach = ApproachResult & {
  /** Epoch ms of the earliest author time of the branch's own commits, as reported by the node; null until a run reports one. */
  firstCommitAt: number | null;
};

export const RUN_STATUSES = ["starting", "running", "succeeded", "failed", "stopped", "budget_exceeded", "superseded"] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/** A one-click follow-up proposed by a routine run (docs/specs/routines.md §6.4). */
export type RoutineProposalDTO = {
  id: string;
  routineId: string;
  routineRunId: string;
  normalizedTitle: string;
  decisionId: string;
  state: RoutineProposalState;
  snoozedUntil: number | null;
  createdAt: number;
};

/** The persisted output and accounting record for one scheduled or manual routine fire. */
export type RoutineRunDTO = {
  id: string;
  routineId: string;
  /** Both are null for the hub-owned empty-day run. */
  taskId: string | null;
  runId: string | null;
  trigger: RoutineRunTrigger;
  windowStart: number;
  windowEnd: number;
  status: RunStatus;
  costUsd: number;
  summary: string | null;
  reportMd: string | null;
  createdAt: number;
  proposals: RoutineProposalDTO[];
};

/** The deliberately small last-run projection returned by the routines list. */
export type RoutineLastRunDTO = Pick<RoutineRunDTO, "id" | "status" | "costUsd" | "createdAt">;

/** Fields accepted by the public create-routine endpoint. */
export type CreateRoutineInput = {
  projectId: string;
  agentId: string;
  name: string;
  instructions: string;
  reads: RoutineReadScope[];
  timeOfDay: string;
  timezone: string;
  maxBudgetUsd: number;
};

/** A routine's project is immutable; every other configured field can be edited. */
export type UpdateRoutineInput = Partial<Omit<CreateRoutineInput, "projectId">>;

/**
 * Fields accepted by `POST /api/office/chief/routines` (docs/specs/chief-of-office.md §12.2): the agent is always the Chief
 * and the routine is office-wide. `maxBudgetUsd` defaults to the spec's $0.75.
 */
export type CreateChiefRoutineInput = Omit<CreateRoutineInput, "projectId" | "agentId" | "maxBudgetUsd"> & { maxBudgetUsd?: number };

/** A daily schedule as presented by the Routines API and UI. */
export type RoutineDTO = {
  id: string;
  /** null for a Chief routine, which is office-wide and reads every project (docs/specs/chief-of-office.md §12.2). */
  projectId: string | null;
  agentId: string;
  name: string;
  instructions: string;
  reads: RoutineReadScope[];
  timeOfDay: string;
  timezone: string;
  maxBudgetUsd: number;
  ownerId: string;
  status: RoutineStatus;
  failureStreak: number;
  dedupeSkips: number;
  lastFiredAt: number | null;
  nextDueAt: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  lastRun: RoutineRunDTO | null;
  cost30dUsd: number;
  /** The open decision explaining `paused_failing`, if any. */
  pausedFailingDecisionId: string | null;
};

/** List rows never carry report text, summaries, windows, task links or proposals. */
export type RoutineListDTO = Omit<RoutineDTO, "lastRun"> & { lastRun: RoutineLastRunDTO | null };

/** A run closed because the task moved to another runtime (docs/specs/runtime-fallback.md §4.3); not a failure. */
export const SUPERSEDED_RUN_STATUS = "superseded" as const;
export type FinishedRunStatus = Exclude<RunStatus, "starting" | "running">;

/** Where a run executes: a node's runner, or the hub driving a Vercel Sandbox (docs/specs/remote-runtime.md). */
export const RUN_HOSTS = ["node", "vercel-sandbox"] as const;
export type RunHost = (typeof RUN_HOSTS)[number];

/** The deliverable kinds of mission-control §3.2 (the same union as `DeliverableKind`). */
export type RunDeliverable = DeliverableKind;

/** What a run's source can do (docs/specs/mission-control.md §5.2). Read by MC5 and MC6; the field name `RunDTO.capabilities` is stable. */
export type RunSourceCapabilities = {
  steer: boolean;                       // live text into the running agent
  stop: boolean;
  toolApproval: boolean;                // the runtime itself can pause a tool call for a human (Managed Agents)
  cost: "exact" | "reported" | "none";  // where costUsd comes from
  deliverables: RunDeliverable[];       // which deliverable kinds it can produce
  network: "allowlist" | "unrestricted-by-default";
  /** An independent reviewer subagent (Claude Code's Task tool) is available to the run (bring-your-own-seats §6.1). */
  subagents: boolean;
  /** The Claude Code advisor tool is available to the run. */
  advisor: boolean;
  /** The per-command guard (a Claude PreToolUse hook) screens the run's Bash commands (bring-your-own-seats §6.2). */
  commandGuard: boolean;
};

/**
 * The capabilities of the source for a run on `host` with `runtime` (the CLI that ran it; null on old rows, read as Claude Code).
 * Only Claude Code runs have a stdin steering channel and a reliable USD figure; every other runtime has neither
 * (docs/specs/runtime-fallback.md §4; apps/server/src/agent/runner.ts `steerable`).
 */
export function runSourceCapabilities(host: RunHost, runtime: Runtime | null): RunSourceCapabilities {
  switch (host) {
    case "vercel-sandbox":
      return { steer: true, stop: true, toolApproval: false, cost: "exact", deliverables: ["code"], network: "allowlist", subagents: true, advisor: true, commandGuard: false };
    case "node": {
      const claude = runtime === null || runtime === "claude-code";
      return {
        steer: claude,
        stop: true,
        toolApproval: false,
        cost: claude ? "exact" : "none",
        deliverables: ["code", "artifact", "check", "action"],
        network: "allowlist",
        // Every capability fails closed: only Claude Code is known to have these (bring-your-own-seats §6.1).
        subagents: claude,
        advisor: claude,
        commandGuard: claude,
      };
    }
    default: {
      const unhandled: never = host;
      throw new Error(`no capabilities for run host ${String(unhandled)}`);
    }
  }
}

export type RunDTO = {
  id: string;
  /** Null on a Chief run: the Chief works on the office, not on a task (docs/specs/chief-of-office.md §4). */
  taskId: string | null;
  projectId: string;
  agentId: string;
  /** The node that runs it. Null for a hub-driven run (`host` is `vercel-sandbox`). */
  nodeId: string | null;
  host: RunHost;
  /** 'review': a delegated review run, read-only, tied to one decision. 'chief': a Chief of Office run, no task. 'build': everything else. */
  kind: "build" | "review" | "chief";
  /** The decision a review run is reviewing. Null on a build run. */
  decisionId: string | null;
  status: RunStatus;
  branch: string | null;
  worktree: string | null;
  model: string | null;
  /** Which CLI ran this run (docs/specs/runtime-fallback.md §4.4). Null on rows written before the field existed. */
  runtime: Runtime | null;
  /** The superseded run this one took over from after a runtime fallback; null otherwise. */
  fallbackFrom: string | null;
  /** A hub restart orphaned this remote run; its sandbox commits must be recovered before the task workspace is replaced. */
  remoteRecoveryPending: boolean;
  sessionId: string | null;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  numTurns: number;
  exitCode: number | null;
  error: string | null;
  summary: string | null;
  stopRequested: boolean;
  startedAt: number;
  finishedAt: number | null;
  lastActivityAt: number;
  eventCount: number;
  /** What this run's source can do; derived from `host` and `runtime`, not stored. */
  capabilities: RunSourceCapabilities;
};

/** Why a run was superseded and the task moved to another runtime (docs/specs/runtime-fallback.md §4.1). */
export const FALLBACK_REASONS = ["rate_limited", "credits_required", "overage", "usage_limit"] as const;
export type FallbackReason = (typeof FALLBACK_REASONS)[number];

/**
 * Event types the runtime fallback writes to the append-only `events` log (docs/specs/runtime-fallback.md §4.4).
 * `run.fallback` is written by the hub when a run starts with `fallbackFrom`; a node cannot post events itself.
 */
export const RUNTIME_FALLBACK_EVENT_TYPES = ["run.fallback", "run.refused", "runtime.sandbox_failed", "runtime.not_logged_in", "egress.denied"] as const;
export type RuntimeFallbackEventType = (typeof RUNTIME_FALLBACK_EVENT_TYPES)[number];

export const RUN_EVENT_KINDS = ["init", "text", "thinking", "tool_use", "tool_result", "system", "result", "stderr", "note"] as const;
export type RunEventKind = (typeof RUN_EVENT_KINDS)[number];

export type RunEventDTO = {
  seq: number;
  runId: string;
  ts: number;
  kind: RunEventKind;
  /** Which conversation this line belongs to: null for the main agent, else the subagent's tool_use id. */
  parent: string | null;
  tool: string | null;
  text: string;
  data: Record<string, unknown> | null;
};

export type TelemetryEvent = "api_request" | "tool_result" | "tool_decision" | "user_prompt";

export type TelemetryDTO = {
  id: number;
  ts: number;
  nodeId: string;
  actorId: string | null;
  sessionId: string | null;
  taskId: string | null;
  runId: string | null;
  event: TelemetryEvent;
  model: string | null;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  durationMs: number | null;
  toolName: string | null;
  success: boolean | null;
  decision: string | null;
};

/** One runtime's detection result as a node reports it (bring-your-own-seats §4.1). Mirrors the node's `RuntimeStatus`; never carries stdout or a path (`bin` is a basename). */
export type NodeRuntimeStatus = {
  runtime: "claude-code" | "codex" | "cursor" | "antigravity";
  installed: boolean;
  version: string | null;
  ownLogin: "logged_in" | "logged_out" | "not_checked";
  plan: string | null;
  connectable: "yes" | "needs_keychain" | "unverified" | "no_config_dir";
  adopted: boolean;
  storeLogin: "valid" | "invalid" | "none";
  probe: "passed" | "failed" | "not_run";
  probeFailedId: string | null;
  smokedAt: number | null;
  enabled: boolean;
  ready: boolean;
  blocker: string | null;
  allowModels: string[];
  defaultModel: string | null;
  egressHosts: string[];
  bin: string;
};

/**
 * One machine-connected service as a node reported it (docs/specs/machine-connected-services.md §3). Capabilities, never
 * credentials: `name` and `scope` (a catalog host), a state, an optional account label and when the node checked.
 * `state` is a `ServiceState` from services.ts; typed as a string here so dto.ts stays free of the catalog.
 */
export type NodeServiceDTO = {
  name: string;
  scope: string;
  state: string;
  /** Account login, at most 80 printable chars, never token-shaped. Null when none was reported or it was dropped. */
  label: string | null;
  checkedAt: number;
};

export type NodeDTO = {
  id: string;
  memberId: string;
  name: string;
  version: string | null;
  claudeVersion: string | null;
  agentIds: string[];
  /** Null = no limit. */
  maxRuns: number | null;
  lastSeenAt: number;
  online: boolean;
  /** "merge_only" runs no agents; it owns the merge queue for `mergeProjectIds` instead. */
  role: "member" | "merge_only";
  /** Projects this node is the registered merge-only node for. Empty for a "member" node. */
  mergeProjectIds: string[];
  /** Projects a "member" node has a checkout of (advertised at registration); empty for a "merge_only" node and for a node that predates this. */
  checkoutProjectIds: string[];
  /** This node's last reported Claude Code login state (#110 AC4). Null until a heartbeat reports
   *  one — always the case for a "merge_only" node, which runs no agents and checks no login. */
  cloudLogin: CloudLoginState | null;
  /** Admission control (#209): true when the node's own last heartbeat measured load/memory past its threshold — it is claiming no new runs, though any run already in flight keeps going. False until the first heartbeat carrying a reading lands. */
  saturated: boolean;
  /** 1-minute load average divided by core count, from the node's last heartbeat; null before the first one carrying a reading. */
  loadPerCore: number | null;
  /** Free memory as a fraction of total, from the node's last heartbeat; null before the first one carrying a reading. */
  freeMemRatio: number | null;
  /** Free disk as a fraction of the volume holding the node's worktrees, from its last heartbeat that carried one (#1378); null before that. */
  diskFreeRatio: number | null;
  /** Landing A2: the node's disk from its last heartbeat; null from a node that does not report it yet. */
  disk: { freeBytes: number; totalBytes: number } | null;
  /** The runtimes this node reported at registration or in its last heartbeat (bring-your-own-seats §4.2); null from a node that predates this ("unknown": the hub neither refuses nor offers). */
  runtimes: NodeRuntimeStatus[] | null;
  /** Connected services the node last reported (empty before the first report). An offline node reads `unknown` for every one: a machine that cannot be reached is never "not logged in". */
  services?: NodeServiceDTO[];
};

export type GitCommitRef = { sha: string; message: string; at: number };

export type GitMainSnapshot = {
  sha: string | null;
  message: string | null;
  at: number | null;
  hasOrigin: boolean;
  /** main's position vs origin/main */
  ahead: number;
  behind: number;
};

export type GitBranchSnapshot = {
  name: string;
  /** Task number parsed from an `rm/<number>-*` branch name, if it follows that convention. */
  taskNumber: number | null;
  /** Resolved by the hub against the project's tasks when the snapshot is stored. */
  taskId: string | null;
  ahead: number;
  behind: number;
  lastCommit: GitCommitRef | null;
  filesChanged: number;
  insertions: number;
  deletions: number;
  merged: boolean;
  /** Whether merging this branch into main right now would conflict (`git merge-tree --write-tree`). */
  conflicts: boolean;
};

export type GitWorktreeSnapshot = { path: string; branch: string | null; dirty: boolean };

/** What a node computes locally from its checkout before reporting to the hub. */
/** One commit of the history graph, newest first in topological order. */
export type GitGraphCommit = { sha: string; parents: string[]; subject: string; author: string; at: number; refs: string[] };

export type GitSnapshotInput = {
  main: GitMainSnapshot;
  branches: Omit<GitBranchSnapshot, "taskId">[];
  /** The node's worktrees, capped (dirty and in-progress first); `worktreesTotal` has the full count. */
  worktrees: GitWorktreeSnapshot[];
  /** Total worktrees on the node before the cap (#373). Absent from nodes that predate it. */
  worktreesTotal?: number;
  /** Recent history of main plus unmerged task branches, for the graph view. */
  graph?: GitGraphCommit[];
  /** Per-file commit counts over the last 30 days, for the codebase map's hot files. Best-effort, capped. */
  churn?: ChurnEntry[];
};

export type GitSnapshotDTO = {
  projectId: string;
  nodeId: string;
  reportedAt: number;
  main: GitMainSnapshot;
  branches: GitBranchSnapshot[];
  worktrees: GitWorktreeSnapshot[];
  worktreesTotal?: number;
  graph?: GitGraphCommit[];
};

export const MAIN_CHECK_STATUSES = ["pass", "fail", "error"] as const;
export type MainCheckStatus = (typeof MAIN_CHECK_STATUSES)[number];
/** 'ci' is reserved for a later GitHub Actions ingest and rejected for now. */
export const MAIN_CHECK_SOURCES = ["merge_queue", "node"] as const;
export type MainCheckSource = (typeof MAIN_CHECK_SOURCES)[number];
/** Which command a check ran (tiered checks, A4a): `fast` = the project's fastCheckCommand, `full` = its checkCommand. */
export const MAIN_CHECK_TIERS = ["fast", "full"] as const;
export type MainCheckTier = (typeof MAIN_CHECK_TIERS)[number];
/** Longest `outputTail` kept, in bytes: the last 8 KB of a check's combined output. */
export const MAIN_CHECK_TAIL_BYTES = 8 * 1024;

/** The result of running the check command against main at one sha (status page, docs/specs/status-page.md §4.1). */
export type MainCheckDTO = {
  id: string;
  projectId: string;
  sha: string;
  status: MainCheckStatus;
  source: MainCheckSource;
  /** Rows written before tiered checks carry no tier: they ran checkCommand, so they read as `full`. */
  tier: MainCheckTier;
  nodeId: string;
  durationMs: number | null;
  checkDurations: CheckDurations;
  outputTail: string | null;
  at: number;
};

export type CostRow = { actorId: string; name: string; kind: Actor["kind"]; costUsd: number; inputTokens: number; outputTokens: number; sessions: number };

export type CostSummary = {
  windowDays: number;
  totalUsd: number;
  byActor: CostRow[];
  byProject: { projectId: string; name: string; costUsd: number; runs: number }[];
  byDay: { day: string; costUsd: number }[];
};

export type Metrics = {
  windowDays: number;
  humanWaitMin: { median: number | null; p90: number | null; samples: number };
  decisions: { open: number; answered: number; overdue: number };
  tasks: { done: number; autoDone: number; autoDoneRate: number | null; reopened: number; byMode: Record<Mode, number> };
  spotChecks: { answered: number; issues: number; defectRate: number | null };
  provenanceCoverage: number | null;
};

/** `GET /api/bridge`: everything that needs a human, and whether the machine is healthy, in one query. */
export type NeedsAHuman = {
  approve: DecisionDTO[];
  advise: DecisionDTO[];
  act: DecisionDTO[];
  steer: SteerItem[];
};

export type AgentLane = {
  actorId: string;
  name: string;
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  runId: string;
  status: RunStatus;
  currentAction: string | null;
  startedAt: number;
  elapsedMin: number;
  costUsd: number;
  acceptance: { met: number; total: number };
};

export type BridgePipeline = { projectId: string; projectName: string; stages: Record<Stage, number> };

/** One command-guard decision (docs/specs/command-guard.md §4.4). `grant-used` logs a granted ask. */
export type GuardDecisionVerdict = "allow" | "block" | "ask" | "grant-used";
export type GuardDecisionMode = "enforce" | "audit";

export type GuardDecisionDTO = {
  id: string;
  ts: number;
  runId: string | null;
  taskId: string | null;
  projectId: string | null;
  agentId: string | null;
  verdict: GuardDecisionVerdict;
  mode: GuardDecisionMode;
  rules: string[];
  reason: string | null;
  command: string;
  commandHash: string;
  cwd: string | null;
  decisionId: string | null;
};

/** `POST /api/runs/:id/guard`: the rows stored by this call (redacted, capped), how many were resends already stored, and whether this call logged guard.disabled. */
export type GuardIngestResultDTO = {
  rows: GuardDecisionDTO[];
  duplicates: number;
  disabledLogged: boolean;
  /** guard.bypassed events newly logged from this batch (the §4.2 addendum's fail-open detection). */
  bypassedLogged?: number;
};

/** `POST /api/runs/:id/guard/ask`: the open risk_acceptance decision for this (task, command hash); `created` is false when an open one already existed. */
export type GuardAskResultDTO = {
  decisionId: string;
  created: boolean;
  commandHash: string;
  assigneeId: string | null;
};

/**
 * `GET /api/tasks/:id/guard-grants?hash=`: the latest guard decision for that (task, hash).
 * granted: approved under 24 h ago and the task is in_progress (`expiresAt` set); declined: kept
 * blocked (`by`/`byName` is who answered); pending: still open; none: no decision, or the grant lapsed.
 */
export type GuardGrantDTO = {
  status: "granted" | "declined" | "pending" | "none";
  decisionId: string | null;
  by: string | null;
  byName: string | null;
  answeredAt: number | null;
  expiresAt: number | null;
};

/** `warning` from 80% of the budget, `exhausted` from 100% (dispatch stops starting runs), `none` with no budget set. */
export type BudgetState = "none" | "ok" | "warning" | "exhausted";

/** One project's month against its budget (MC10a AC3). `month` is the UTC month, `YYYY-MM`. */
export type ProjectBudget = {
  projectId: string;
  projectName: string;
  month: string;
  monthlyBudgetUsd: number | null;
  spendUsd: number;
  state: BudgetState;
};

export const BUDGET_WARN_RATIO = 0.8;

/** The one rule for a month's state, shared by the hub's gate and every view. */
export function budgetState(spendUsd: number, budgetUsd: number | null): BudgetState {
  if (budgetUsd === null) return "none";
  if (spendUsd >= budgetUsd) return "exhausted";
  return spendUsd >= BUDGET_WARN_RATIO * budgetUsd ? "warning" : "ok";
}

/** One row of `chief_findings` (docs/specs/chief-of-office.md §9.2): a scan finding with its dedupe key and cooldown ledger. */
export type ChiefFindingDTO = {
  key: string;
  scan: string;
  subjectId: string;
  stateHash: string;
  firstSeen: number;
  lastSeen: number;
  lastActedAt: number | null;
  actedCount: number;
  /** Null while the finding is current; set when it cleared. */
  closedAt: number | null;
  detail: Record<string, unknown>;
};

/** One row of the "Actions by the Chief" feed (docs/specs/chief-of-office.md §8.1, §8.3). */
export type ChiefActionDTO = {
  id: string;
  at: number;
  chiefId: string;
  projectId: string | null;
  tool: string;
  subjectType: string | null;
  subjectId: string | null;
  input: unknown;
  reason: string;
  trigger: string;
  reversible: boolean;
  /** Set when `reversible` is false: what the feed says instead of an Undo button. */
  irreversibleWhy: string | null;
  undoneAt: number | null;
  undoneBy: string | null;
  /** For a reversible, not-yet-undone row: what differs from the state right after the action ("changed since, not undone"); null when unchanged. */
  changedSince: Record<string, { expected: unknown; actual: unknown }> | null;
  runId: string | null;
  costUsd: number;
};

/** Bumped when `GET /api/chief/glance` changes shape in a way an older mod cannot read (docs/specs/chief-of-office.md §12.5.4). */
export const CHIEF_GLANCE_API_VERSION = 1;

/** A decision as the glance shows it. `options` is the decision's own list, the one `POST /api/decisions/:id/answer` validates against. */
export type ChiefGlanceDecision = {
  id: string;
  projectId: string;
  taskId: string | null;
  kind: DecisionKind;
  question: string;
  blocking: boolean;
  askedBy: string;
  createdAt: number;
  ageMin: number;
  options: DecisionOption[];
  /** Ids of options the answer endpoint refuses without a rationale (400 `rationale_required`); the mod prompts for one. */
  rationaleRequired: string[];
  /** The option id the asker marked as recommended, as the web shows it; null when none. */
  recommendation: string | null;
  /** True only for `question` and only for someone who may answer: every other kind is answered in the web, where the context is (§12.5.3). */
  answerable: boolean;
  /** Changes whenever something an answer depends on changes; the mod sends it back as `expectVersion` (C21-AC3). */
  version: string;
  chiefNote: ChiefNoteDTO | null;
};

/** `GET /api/chief/glance` (C16): everything the web and the mod show at a glance, for the caller only, in one read. */
export type ChiefGlanceDTO = {
  apiVersion: number;
  generatedAt: number;
  /** `count` is every open decision of the caller; `items` is capped (the first 50, inbox order). */
  decisions: { count: number; first: ChiefGlanceDecision | null; items: ChiefGlanceDecision[] };
  agentLanes: AgentLane[];
  /** The Bridge's Steer list: stalled runs, failed verifications, budget hits, overlaps. */
  stuck: SteerItem[];
  today: { costUsd: number; runs: number; chief: { spentUsd: number; capUsd: number } | null };
  chief: import("./types.ts").ChiefStatusDTO | null;
  actions: ChiefActionDTO[];
  /** The caller's open decisions that the Chief asked (approve a split, accept a follow-up, pause or resume a routine). */
  proposals: ChiefGlanceDecision[];
};

/** `office_snapshot` (docs/specs/chief-of-office.md §6.2): exactly what `GET /api/bridge` and `GET /api/mission` return, from the same service functions. */
export type OfficeSnapshotDTO = { bridge: BridgeView; mission: import("./mission.ts").MissionView };

export type BridgeView = {
  needsAHuman: NeedsAHuman;
  agentLanes: AgentLane[];
  pipeline: BridgePipeline[];
  today: { costUsd: number; runs: number; nodes: number; budgets: ProjectBudget[] };
};

/** A correction row as the hub returns it (correction-lessons.md §2.1). Findings are capped at 20 items. */
export interface CorrectionDTO {
  id: string;
  projectId: string;
  decisionId: string;
  taskId: string;
  /** The task's builder at answer time. */
  agentId: string;
  kind: CorrectionKind;
  rationale: string;
  findings: CorrectionFinding[];
  status: CorrectionStatus;
  attempts: number;
  lessonId: string | null;
  ruleKey: string | null;
  recurrence: boolean;
  /** Correction lessons live for this agent when the correction was recorded; recurrence is judged against it. */
  lessonSnapshot: CorrectionLessonSnapshotEntry[];
  createdAt: number;
  draftedAt: number | null;
}

/** One decision that taught a correction rule (correction-lessons.md §2.5). */
export interface CorrectionSourceDTO {
  correctionId: string;
  decisionId: string;
  taskId: string;
  taskNumber: number;
  kind: CorrectionKind;
  answeredBy: string;
  answeredAt: number;
  recurrence: boolean;
}

/** A correction rule scoped to one agent, with its source decisions and how it has worked since going live (correction-lessons.md §2.5). */
export interface CorrectionRuleDTO {
  lesson: LessonDTO;
  sources: CorrectionSourceDTO[];
  /** Distinct build runs of this agent given this lesson since `wentLiveAt`. */
  runsSinceLive: number;
  /** Corrections of this agent marked recurrence for this lesson. */
  recurrencesSinceLive: number;
}
